import React, { createContext, useContext, useReducer, useCallback, useMemo, ReactNode } from 'react';

// Define the state interface
interface AppState {
  unreadCount: number;
  // Add more state properties here as needed
  // e.g., theme: 'light' | 'dark';
  // onlineStatus: boolean;
}

// Define action types
type AppAction =
  | { type: 'SET_UNREAD_COUNT'; payload: number }
  | { type: 'INCREMENT_UNREAD_COUNT' }
  | { type: 'DECREMENT_UNREAD_COUNT' }
  | { type: 'RESET_UNREAD_COUNT' };
  // Add more action types here as needed

// Define the context interface
interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  // Convenience methods
  setUnreadCount: (count: number) => void;
  incrementUnreadCount: () => void;
  decrementUnreadCount: () => void;
  resetUnreadCount: () => void;
}

// Initial state
const initialState: AppState = {
  unreadCount: 0,
};

// Reducer function
const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'SET_UNREAD_COUNT':
      return {
        ...state,
        unreadCount: action.payload,
      };
    case 'INCREMENT_UNREAD_COUNT':
      return {
        ...state,
        unreadCount: state.unreadCount + 1,
      };
    case 'DECREMENT_UNREAD_COUNT':
      return {
        ...state,
        unreadCount: Math.max(0, state.unreadCount - 1),
      };
    case 'RESET_UNREAD_COUNT':
      return {
        ...state,
        unreadCount: 0,
      };
    default:
      return state;
  }
};

// Create the context
const AppContext = createContext<AppContextType | undefined>(undefined);

// Provider component
interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Convenience methods - memoized to prevent infinite re-renders
  const setUnreadCount = useCallback((count: number) => {
    dispatch({ type: 'SET_UNREAD_COUNT', payload: count });
  }, [dispatch]);

  const incrementUnreadCount = useCallback(() => {
    dispatch({ type: 'INCREMENT_UNREAD_COUNT' });
  }, [dispatch]);

  const decrementUnreadCount = useCallback(() => {
    dispatch({ type: 'DECREMENT_UNREAD_COUNT' });
  }, [dispatch]);

  const resetUnreadCount = useCallback(() => {
    dispatch({ type: 'RESET_UNREAD_COUNT' });
  }, [dispatch]);

  const contextValue: AppContextType = useMemo(() => ({
    state,
    dispatch,
    setUnreadCount,
    incrementUnreadCount,
    decrementUnreadCount,
    resetUnreadCount,
  }), [state, dispatch, setUnreadCount, incrementUnreadCount, decrementUnreadCount, resetUnreadCount]);

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

// Custom hook to use the app context
export const useAppStore = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppStore must be used within an AppProvider');
  }
  return context;
};

// Export types for use in other components
export type { AppState, AppAction, AppContextType };